/**
 * The `AI Drafted Report` panel — `05-reporting-marking.md` §B6, drawn to the agreed mockup
 * `docs/design/official-profile-detail-v1.html`.
 *
 * The mockup puts this panel in the Reporting Section's right-hand column, immediately above
 * `TD Draft Report`, and gives it a head chip, a `readonly` textarea, a `Read-Only` tag, a
 * `{n} / 800` character counter, a `Copy` button and the hidden note explaining that the draft
 * is not editable here. All of that is here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 45 — THE DRAFTING RULES ARRIVED, AND SO DID THE BUTTON
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The head chip used to read `◆ Drafting Rules: To Be Supplied`, which was true when it was
 * written and stopped being true on 11 August 2026, when ruling CC-Q17 supplied them: a length
 * target of ~700–800 characters and a content checklist selected by the official's grading
 * role, with `05a-marking-criteria-reference.md` governing the register. The API encodes both
 * (`apps/api/src/ai/prompts.ts`), so the chip now says what the draft is actually made of —
 * **`◆ Drafts From Your Marks And Quick Notes`** — because a chip that still promised rules
 * "to be supplied" would be telling a Technical Delegate that a feature he can press does not
 * know what it is doing.
 *
 * **`Draft From Feedback` is the button, and it is not called `Draft With AI`.** The panel is
 * already titled `AI Drafted Report`; a button repeating the technology would say the thing on
 * screen twice and the thing that matters — *what this is built from* — not at all. It names
 * the evidence, which is also the honest answer to "why is my draft thin": there was little
 * feedback.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR RULES THIS COMPONENT KEEPS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **A failed press never loses the draft on screen.** `12-ai-features.md` §5's failure
 *     clause — *"the evidence is always readable and the report is always writable by hand"* —
 *     and §1.2's rule that a failure never loses anything. The server writes nothing on a
 *     failure, and this component replaces its report only from a **successful** reply, so a
 *     refused regeneration leaves the previous draft exactly where it was with a line saying
 *     what happened.
 *  2. **An installation with no AI service explains itself; it does not fail.** `aiConfigured`
 *     travels on the report body, so the control is withheld and a sentence stands where it
 *     would have been. A button that 503s teaches the TD that the Control Centre is broken,
 *     when in fact this deployment simply has no key.
 *  3. **On an umpire's report there is no button at all**, and the panel says who has one. An
 *     umpire's report is drafted and owned by the owning Umpires Manager (§A5, write path W3)
 *     and the API refuses a TD draft on it with a 403 — so drawing the control would be
 *     offering a press the server will refuse, which is the same defect `ReportComposer`
 *     refuses to commit with `Push To TMS`.
 *  4. **The draft is still not editable here.** It is copied into the TD's own box below and
 *     edited there, so the generated text and his version stay distinguishable. Nothing in
 *     this run changed that; the textarea is still `readOnly`.
 *
 * **The counter is the same contract as the composer's** — `LIMIT` and `NEAR` are imported
 * from `ReportComposer` rather than restated, so there is one answer to "how much is left".
 * The server trims an over-long draft at a sentence boundary before storing it, so the counter
 * should not normally see an `over` state on a generated draft; if it does, the number is
 * telling the truth and `truncated` explains the case where the trim happened.
 */

import { useState } from "react";

import { ApiError, generateAiDraft } from "../../api/client.js";
import type { ReportAiDraftBody, ReportBody } from "../../api/types.js";
import { counterClass, counterTitle, LIMIT } from "./ReportComposer.js";

/** Copy to the clipboard, with §B6's `execCommand` fallback for a refused permission. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Injected in tests only; the real press is `generateAiDraft`. */
export type AiDraftGenerator = (tmsPersonId: string) => Promise<ReportAiDraftBody>;

export interface AiDraftPanelProps {
  /**
   * The report as fetched. Held as seed state, which is safe for the reason `ReportComposer`
   * gives: the Report Studio mounts this panel under a key of the official's id, so a
   * different official is a different component instance rather than a mutated one.
   */
  report: ReportBody;
  /**
   * Told when a draft lands, so the page's other panels can be handed the authoritative
   * report. Optional: the panel is complete without it and simply keeps the new draft to
   * itself.
   */
  onDrafted?: (report: ReportBody) => void;
  generate?: AiDraftGenerator;
}

/**
 * The sentence shown when a press fails.
 *
 * The API's own message is used where it sent one — `ai/gateway.ts` writes those for the person
 * reading them, and they say which of "no key", "timed out", "declined" and "could not be
 * reached" happened. This is the fallback for a failure that carried no message of its own, and
 * it says the one thing that is always true and always the most important: nothing was lost.
 */
const GENERIC_FAILURE =
  "The draft could not be generated. Nothing was lost — write it yourself below, or try again.";

export function AiDraftPanel({
  report: initialReport,
  onDrafted,
  generate = generateAiDraft,
}: AiDraftPanelProps): React.JSX.Element {
  const [report, setReport] = useState<ReportBody>(initialReport);
  const [copied, setCopied] = useState<"" | "ok" | "blocked">("");
  const [busy, setBusy] = useState<boolean>(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [truncated, setTruncated] = useState<boolean>(false);

  const text = report.aiDraftText ?? "";

  async function press(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      const drafted = await generate(report.tmsPersonId);
      // Only a SUCCESS replaces what is on screen. Rule 1 — a refusal leaves the previous
      // draft standing, because the server wrote nothing and the panel must not either.
      setReport(drafted.report);
      setTruncated(drafted.truncated);
      setCopied("");
      onDrafted?.(drafted.report);
    } catch (error) {
      // The API's own sentence where there is one; it names which failure this was and what
      // to do about it. `ApiError` is the only thing that carries one.
      setFailure(error instanceof ApiError ? error.message : GENERIC_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="panel panel--d3"
      aria-labelledby="aiTitle"
      data-testid="ai-draft"
      data-howto="ai-draft"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="aiTitle">
          AI Drafted Report
        </h3>
        <span className="spacer" />
        <span className="tbschip">
          <span aria-hidden="true">◆</span>Drafts From Your Marks And Quick Notes
        </span>
      </div>

      <div className="airep">
        <textarea
          className="aitext"
          aria-label="AI drafted report"
          data-testid="ai-text"
          readOnly
          value={text}
          placeholder="No AI draft has been generated for this official yet."
        />
        <div className="aibar">
          <span className="readonly-tag">Read-Only</span>
          <span
            className={counterClass(text.length)}
            title={counterTitle(text.length)}
            data-testid="ai-charcount"
          >
            {text.length} / {LIMIT}
          </span>
          <span className="spacer" style={{ marginLeft: "auto" }} />
          {/*
            Rules 2 and 3: the control exists only where a press could succeed. On an umpire's
            report it is the owning UM's to make (§A5), and on an installation with no AI
            service there is nothing to press — both say so in words below rather than drawing
            a button that leads to a refusal.
          */}
          {report.ownedByUm || !report.aiConfigured ? null : (
            <button
              type="button"
              className="gbtn"
              data-testid="ai-draft-generate"
              disabled={busy}
              onClick={() => {
                void press();
              }}
            >
              {busy ? "Drafting…" : text === "" ? "Draft From Feedback" : "Draft Again"}
            </button>
          )}
          <button
            type="button"
            className="gbtn"
            data-testid="copy-ai"
            disabled={text === ""}
            onClick={() => {
              void copyText(text).then((ok) => {
                setCopied(ok ? "ok" : "blocked");
              });
            }}
          >
            Copy
          </button>
        </div>

        {busy ? (
          <span className="btnhint" role="status" data-testid="ai-draft-busy">
            Reading your marks and quick notes — this takes a few seconds. Nothing else is
            blocked while it runs.
          </span>
        ) : null}

        {failure === null ? null : (
          <span className="btnhint" role="status" data-testid="ai-draft-failure">
            <b>Not generated —</b> {failure}
          </span>
        )}

        {truncated ? (
          <span className="btnhint" role="status" data-testid="ai-draft-truncated">
            <b>Trimmed to fit —</b> the draft came back longer than the {LIMIT}-character TMS
            field and was cut back at the end of a sentence. Read the last line before you use
            it.
          </span>
        ) : null}

        {report.ownedByUm ? (
          <span className="btnhint" data-testid="ai-draft-owned-by-um">
            <b>Drafted by the owning Umpires Manager —</b> an umpire&rsquo;s report is hers to
            draft and to push (write path W3), so the draft is generated from her Report Studio
            and not from here. Your surfaces on this row are the TD Closing Remarks and the
            mark agreement.
          </span>
        ) : null}

        {!report.ownedByUm && !report.aiConfigured ? (
          <span className="btnhint" data-testid="ai-draft-unconfigured">
            <b>AI drafting is not switched on for this installation —</b> there is no drafting
            service attached to this Control Centre, so there is nothing to press. Everything
            else on this screen works exactly as it would with one; write the report in your
            own box below.
          </span>
        ) : null}

        {copied === "" ? null : (
          <span className="btnhint" role="status" data-testid="ai-copy-result">
            {copied === "ok"
              ? `Copied · ${text.length} characters on the clipboard — paste into your TD Draft Report below.`
              : "Copy blocked · Select the text and copy manually."}
          </span>
        )}

        <div className="hidenote">
          <b>Generated from the feedback above —</b> the draft only draws on categories that
          actually have recorded feedback, and a checklist point with nothing behind it is left
          out rather than invented. It is not editable here — copy it into your own draft below
          and edit there, so the generated text and your version stay distinguishable.
        </div>
      </div>

      <div className="panel__foot">
        <b>What it is drafted from —</b> your marks for this official and every one of your own
        quick notes about them, with the notes filed under <b>General</b> read as your running
        summary for the report. The content checklist follows the official&rsquo;s role and the
        register follows the marking-criteria reference; no mark or score is ever written into
        the prose. <b>Not built in this run —</b> the mockup&rsquo;s <b>Your Additional
        Comments</b> box sits inside the Marking Panel above, and there is nowhere to keep what
        you would type into it, so it is not drawn.
      </div>
    </section>
  );
}
